import { Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Redirect, useHistory } from 'react-router-dom'
import { useCenterStore } from '@caradasstore/CenterStore'
import useEntitlementStore from '@caradasstore/EntitlementStore'
import { getCalibrationCenter } from '../../api/api'
import { createCheckoutSession } from '../../api/billingApi'
import { PlanGrid } from '../billing/PlanGrid'
import { PromoCodeField, promoCovers } from '../billing/PromoCodeField'
import { cardFeatures, introLine, recommendedTier } from '../../data/catalog'
import { useCatalog } from '../../store/CatalogStore'
import { getFriendlyErrorMessage } from '../../utils/errorMessage'
import { PlanCard, centerRecommended, pickerTiers } from './ChoosePlan'
import { ErrorBox, RegistrationShell, linkButtonClass } from './RegistrationShell'

/**
 * /packages — where a new owner lands after confirming their email (signed in, no package yet):
 * the same cards as sign-up (Free Trial · AI Complete · AI Report), bought through the location's
 * Billing Checkout; Stripe returns to Settings → Billing, which starts the package and unlocks
 * everything. "Skip for now" goes to the app, where everything stays locked until they choose.
 */
export const ChoosePackage = () => {
  const history = useHistory()
  const ccId = useCenterStore((store) => store.ccId)
  const storedName = useCenterStore((store) => (store.centerDetail?.id === store.ccId ? store.centerDetail?.name : null))
  const [centerName, setCenterName] = useState(storedName)
  const { hasSubscription, loadedForCcId, loadEntitlements } = useEntitlementStore()
  const { catalog, status: catalogStatus } = useCatalog()
  const [pending, setPending] = useState(false)
  const [error, setError] = useState(null)
  const [promo, setPromo] = useState(null)

  useEffect(() => {
    if (ccId) loadEntitlements(ccId)
  }, [ccId, loadEntitlements])

  // Straight from sign-up the center's details aren't loaded yet: fetch its name for the title.
  useEffect(() => {
    if (!ccId || centerName) return
    let active = true
    getCalibrationCenter({ ccid: ccId })
      .then((response) => active && response?.data?.name && setCenterName(response.data.name))
      .catch(() => {})
    return () => {
      active = false
    }
  }, [ccId, centerName])

  // Already has a package (e.g. came back here later): nothing to choose.
  if (ccId && loadedForCcId === ccId && hasSubscription) {
    return <Redirect to='/' />
  }

  const plans = centerRecommended(pickerTiers(catalog))
  const lead = introLine(recommendedTier(catalog))

  const onStart = async (tierId) => {
    setPending(true)
    setError(null)
    try {
      // A promo code only rides the plans it covers (never the Free Trial).
      const covered = promoCovers(
        promo,
        (catalog?.tiers ?? []).find((t) => t.id === tierId)
      )
      const result = covered ? await createCheckoutSession(ccId, tierId, promo.code) : await createCheckoutSession(ccId, tierId)
      if (!result?.url) throw new Error('Checkout did not return a URL.')
      window.location.assign(result.url)
    } catch (err) {
      setPending(false)
      setError(getFriendlyErrorMessage(err))
    }
  }

  return (
    <RegistrationShell
      width='plans'
      title={centerName ? `Choose a package for ${centerName}` : 'Choose your package'}
      subtitle={
        lead
          ? `Your account is ready. ${lead}, then the plan's monthly price — manage or cancel any time in Settings → Billing.`
          : 'Your account is ready. Pick a plan to subscribe — billed monthly from today; manage or cancel any time in Settings → Billing.'
      }
    >
      {error && (
        <div className='mb-5'>
          <ErrorBox>{error}</ErrorBox>
        </div>
      )}
      {catalogStatus !== 'ready' && plans.length === 0 ? (
        <div className='flex justify-center py-10' aria-busy='true'>
          <Loader2 className='h-6 w-6 animate-spin text-white/70' />
        </div>
      ) : (
        <PlanGrid count={plans.length} className='md:py-4'>
          {plans.map((tier) => (
            <PlanCard
              key={tier.id}
              tier={tier}
              features={cardFeatures(tier)}
              pending={pending}
              onStart={onStart}
              promoDays={promoCovers(promo, tier) ? promo.freeDays : undefined}
            />
          ))}
        </PlanGrid>
      )}
      {ccId && (
        <div className='mt-6 flex justify-center'>
          <div className='w-full max-w-[520px]'>
            <PromoCodeField ccId={ccId} applied={promo} onApplied={setPromo} tone='dark' align='center' disabled={pending} />
          </div>
        </div>
      )}
      <p className='mt-5 text-center text-[12px] text-white/45'>Payments are processed securely by Stripe. Prices in USD.</p>
      <div className='mt-3 text-center'>
        <button type='button' onClick={() => history.push('/')} className={linkButtonClass}>
          Skip for now — everything stays locked until you choose a package
        </button>
      </div>
    </RegistrationShell>
  )
}
